/**
 * The public, language-prefixed form of an internal href that is not a router <Link> (a plain <a> built by
 * hand, an href inside translated HTML or MDX). Links rendered by the router get it from its rewrite
 * (localised-url-rewrite.ts); these have to ask for it.
 *
 * Environment-neutral like localised-path.ts: no window, document or React.
 */
import type { LanguageCode } from '../../texts/languages';
import { toLocalisedPath } from './localised-path';
import { sectionKeys } from './slugs';

/**
 * `href` in the public form of `language`: `/compatibility` → `/de/kompatibilitaet`, `/` → `/de/`, query and
 * hash kept. Left as it is: no language (the address names none, as on the dev server, so internal paths
 * still work), anything not a site-relative path (`https://…`, `//host/…`, `#id`, `mailto:`, `tel:`,
 * relative paths) and site-relative paths outside the localised sections (files under public/, an already
 * prefixed `/de/…`).
 */
export function localiseHref(language: LanguageCode | undefined, href: string): string {
  if (!language || !href.startsWith('/') || href.startsWith('//')) {
    return href;
  }
  const end = href.search(/[?#]/);
  const pathname = end === -1 ? href : href.slice(0, end);
  const section = pathname.split('/')[1];
  if (pathname !== '/' && !(sectionKeys as readonly string[]).includes(section)) {
    return href;
  }

  return toLocalisedPath(language, href);
}

const HREF_ATTRIBUTE = /(\shref\s*=\s*)(["'])(.*?)\2/gi;

/** Every `href="…"` of an HTML string through localiseHref, e.g. the links in a translated FAQ answer. */
export function localiseHtmlHrefs(language: LanguageCode | undefined, html: string): string {
  if (!language) {
    return html;
  }

  return html.replace(
    HREF_ATTRIBUTE,
    (_match, before: string, quote: string, href: string) => `${before}${quote}${localiseHref(language, href)}${quote}`,
  );
}
